import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, Package, Save, X } from "lucide-react";
import { useAuth, API } from "@/App";
import BottomNav from "./BottomNav";
import { AppSidebar, SidebarMenuButton } from "./AppSidebar";

const authFetch = async (url, options = {}) => {
  const storedToken = localStorage.getItem("session_token");
  const headers = {
    ...options.headers,
    ...(storedToken ? { "Authorization": `Bearer ${storedToken}` } : {}),
  };
  return fetch(url, { ...options, headers });
};

export default function SuppliesPage() {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [supplies, setSupplies] = useState([]);
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [loading, setLoading] = useState(true);
  const debounceTimers = useRef({});

  const fetchSupplies = async () => {
    try {
      const res = await authFetch(`${API}/supplies`);
      if (res.ok) setSupplies(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchSupplies(); }, []);
  useEffect(() => () => Object.values(debounceTimers.current).forEach(clearTimeout), []);

  const updateSupply = async (id, data) => {
    const res = await authFetch(`${API}/supplies/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    if (!res.ok) toast.error("Salvestamine ebaõnnestus");
  };

  const debouncedUpdate = useCallback((id, field, value) => {
    const key = `${id}_${field}`;
    if (debounceTimers.current[key]) clearTimeout(debounceTimers.current[key]);
    debounceTimers.current[key] = setTimeout(() => {
      updateSupply(id, { [field]: value });
    }, 600);
  }, []);

  const addSupply = async () => {
    const name = newName.trim();
    if (!name) return;
    const res = await authFetch(`${API}/supplies`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name, quantity_tallinn: "", quantity_parnu: "" }),
    });
    if (res.ok) {
      const s = await res.json();
      setSupplies(prev => [...prev, s]);
      setNewName("");
      toast.success("Tarvik lisatud");
    }
  };

  const deleteSupply = async (id) => {
    const res = await authFetch(`${API}/supplies/${id}`, { method: "DELETE" });
    if (res.ok) {
      setSupplies(prev => prev.filter(s => s.supply_id !== id));
      toast.success("Kustutatud");
    }
  };

  const startEdit = (s) => { setEditingId(s.supply_id); setEditName(s.name); };
  const saveEdit = (id) => {
    if (editName.trim()) {
      updateSupply(id, { name: editName.trim() });
      setSupplies(prev => prev.map(s => s.supply_id === id ? { ...s, name: editName.trim() } : s));
    }
    setEditingId(null);
  };

  const handleQtyChange = (id, field, value) => {
    setSupplies(prev => prev.map(s => s.supply_id === id ? { ...s, [field]: value } : s));
    debouncedUpdate(id, field, value);
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-16 md:pb-0">
      <AppSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="md:ml-64 min-h-screen">
        <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
          <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
            <SidebarMenuButton onClick={() => setSidebarOpen(true)} />
            <h1 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <Package className="w-5 h-5" /> MedTarvikud
            </h1>
          </div>
        </header>

      <main className="max-w-6xl mx-auto p-3 md:p-6">
        {/* Add new supply */}
        <div className="flex gap-2 mb-4">
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Uue tarviku nimi..."
            onKeyDown={(e) => e.key === "Enter" && addSupply()}
            className="text-base"
            data-testid="new-supply-input"
          />
          <Button onClick={addSupply} className="bg-slate-900 hover:bg-slate-800 flex-shrink-0" data-testid="add-supply-btn">
            <Plus className="w-4 h-4 mr-1" /> <span className="hidden sm:inline">Lisa</span>
          </Button>
        </div>

        {/* Desktop table */}
        <div className="hidden md:block">
          <Card className="border-slate-200" data-testid="supply-table-card">
            <CardContent className="p-0">
              {supplies.length === 0 ? (
                <div className="p-8 text-center text-slate-400">Tarvikuid pole lisatud</div>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3">Tarvik</th>
                      <th className="text-center text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3 w-32">Tallinn</th>
                      <th className="text-center text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3 w-32">Pärnu</th>
                      <th className="w-20"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {supplies.map((s) => (
                      <tr key={s.supply_id} className="group hover:bg-slate-50/50" data-testid={`supply-row-${s.supply_id}`}>
                        <td className="px-4 py-3">
                          {editingId === s.supply_id ? (
                            <div className="flex items-center gap-2">
                              <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="flex-1" autoFocus
                                onKeyDown={(e) => e.key === "Enter" && saveEdit(s.supply_id)} />
                              <Button size="sm" variant="ghost" onClick={() => saveEdit(s.supply_id)}><Save className="w-4 h-4 text-emerald-600" /></Button>
                              <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}><X className="w-4 h-4" /></Button>
                            </div>
                          ) : (
                            <span className="text-sm font-medium text-slate-800">{s.name}</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <Input
                            className="w-full text-center text-sm"
                            placeholder="—"
                            value={s.quantity_tallinn || ""}
                            onChange={(e) => handleQtyChange(s.supply_id, "quantity_tallinn", e.target.value)}
                            data-testid={`qty-tallinn-${s.supply_id}`}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <Input
                            className="w-full text-center text-sm"
                            placeholder="—"
                            value={s.quantity_parnu || ""}
                            onChange={(e) => handleQtyChange(s.supply_id, "quantity_parnu", e.target.value)}
                            data-testid={`qty-parnu-${s.supply_id}`}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1 justify-end opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => startEdit(s)} className="p-1.5 hover:bg-slate-100 rounded" data-testid={`edit-${s.supply_id}`}>
                              <Edit2 className="w-3.5 h-3.5 text-slate-500" />
                            </button>
                            <button onClick={() => deleteSupply(s.supply_id)} className="p-1.5 hover:bg-red-50 rounded" data-testid={`delete-${s.supply_id}`}>
                              <Trash2 className="w-3.5 h-3.5 text-red-500" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden space-y-2">
          {loading ? (
            <div className="p-8 text-center text-slate-400">Laadib...</div>
          ) : supplies.length === 0 ? (
            <Card className="border-slate-200">
              <CardContent className="p-8 text-center text-slate-400">Tarvikuid pole lisatud</CardContent>
            </Card>
          ) : (
            supplies.map((s) => (
              <Card key={s.supply_id} className="border-slate-200" data-testid={`mobile-supply-${s.supply_id}`}>
                <CardContent className="p-3">
                  {editingId === s.supply_id ? (
                    <div className="flex items-center gap-2">
                      <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="flex-1 text-base" autoFocus
                        onKeyDown={(e) => e.key === "Enter" && saveEdit(s.supply_id)} />
                      <Button size="sm" variant="ghost" onClick={() => saveEdit(s.supply_id)}><Save className="w-4 h-4 text-emerald-600" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}><X className="w-4 h-4" /></Button>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-slate-900">{s.name}</span>
                        <div className="flex gap-1">
                          <button onClick={() => startEdit(s)} className="p-2 hover:bg-slate-100 rounded">
                            <Edit2 className="w-4 h-4 text-slate-500" />
                          </button>
                          <button onClick={() => deleteSupply(s.supply_id)} className="p-2 hover:bg-red-50 rounded">
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </button>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-xs text-slate-500 mb-1 block">Tallinn</label>
                          <Input
                            className="text-base"
                            placeholder="—"
                            value={s.quantity_tallinn || ""}
                            onChange={(e) => handleQtyChange(s.supply_id, "quantity_tallinn", e.target.value)}
                            data-testid={`mobile-qty-tallinn-${s.supply_id}`}
                          />
                        </div>
                        <div>
                          <label className="text-xs text-slate-500 mb-1 block">Pärnu</label>
                          <Input
                            className="text-base"
                            placeholder="—"
                            value={s.quantity_parnu || ""}
                            onChange={(e) => handleQtyChange(s.supply_id, "quantity_parnu", e.target.value)}
                            data-testid={`mobile-qty-parnu-${s.supply_id}`}
                          />
                        </div>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </main>
      </div>
      <BottomNav />
    </div>
  );
}
